import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactElement, useState } from "react";
import tw, { css, styled } from "twin.macro";

import Container from "./Container";
import Section, {
  SectionColor,
  SectionContent,
  SectionHeader,
  SectionHeadline,
  SectionHeadlineHighlight,
} from "./Section";

const Wrapper = styled.div`
  ${tw`font-subtitle font-normal self-stretch mt-[50px]`}

  font-size: 21px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

interface FaqsQuestion {
  question: string;
  answer: React.ReactElement;
}

export const FaqsInner: React.FunctionComponent<{
  questions: FaqsQuestion[];
}> = ({ questions }) => {
  const [openQuestions, setOpenQuestions] = useState<Record<string, boolean>>(
    questions.reduce((prev, curr) => ({ ...prev, [curr.question]: false }), {})
  );

  return (
    <Wrapper>
      {questions.map((q) => (
        <div
          key={q.question}
          tw="pb-6 mb-6"
          css={css`
            border-bottom: 1px solid #e0e0e0;
            &:last-child {
              border-bottom: none;
            }
          `}
        >
          <div
            tw="flex justify-between gap-4"
            css={css`
              cursor: pointer;
              transition: all 200ms;
              &:hover {
                color: #232e3b;
              }
            `}
            onClick={() =>
              setOpenQuestions({
                ...openQuestions,
                [q.question]: !openQuestions[q.question],
              })
            }
          >
            <div
              tw="font-title"
              css={css`
                color: ${openQuestions[q.question] ? "#232e3b" : "inherit"};
              `}
            >
              {q.question}
            </div>
            <div
              tw="min-w-[18px]"
              css={css`
                color: ${openQuestions[q.question] ? "#232e3b" : "inherit"};
              `}
            >
              <FontAwesomeIcon
                icon={openQuestions[q.question] ? faMinus : faPlus}
              />
            </div>
          </div>
          <div
            css={css`
              transition: all 200ms;
              overflow: hidden;
              padding-top: ${openQuestions[q.question] ? "1rem" : "0"};
              height: ${openQuestions[q.question] ? "auto" : "0"};
              max-width: 40rem;

              a {
                font-weight: normal;
                color: #2f75ff;
              }
            `}
          >
            {q.answer}
          </div>
        </div>
      ))}
    </Wrapper>
  );
};

interface FaqsProps {
  title: ReactElement;
  questions: FaqsQuestion[];
  color?: SectionColor;
}

const Faqs: React.FunctionComponent<FaqsProps> = ({
  title,
  questions,
  color = SectionColor.White,
}) => (
  <Container>
    <Section color={color}>
      <SectionHeader>
        <SectionHeadline>
          {title} <SectionHeadlineHighlight>FAQs</SectionHeadlineHighlight>
        </SectionHeadline>
      </SectionHeader>

      <SectionContent>
        <FaqsInner questions={questions} />
      </SectionContent>
    </Section>
  </Container>
);

export default Faqs;
